import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { api, ApiError, unwrap } from "../api/client";
import { qk } from "../api/keys";
import { getSlot } from "../lib/identity";
import { startLive } from "../lib/live";
import { useMe } from "../lib/me";
import { ActionResult } from "./announce";
import { IdentityList, IdentityMenu, useSwitchIdentity } from "./IdentityMenu";

const NAV: [string, string][] = [
  ["/board/videos", "Videoclips"],
  ["/board/songs", "Songs"],
  ["/board/renders", "Renders"],
  ["/choose", "Choose"],
  ["/calendar", "Calendar"],
  ["/analytics", "Analytics"],
  ["/tiktok", "TikTok"],
  ["/jobs", "Jobs"],
  ["/settings", "Settings"],
  ["/agent", "Agent"],
];

function NotLoggedIn() {
  const switchTo = useSwitchIdentity();
  const slot = getSlot();
  return (
    <main className="login-needed">
      <h1>Vezérlőpult</h1>
      <p>
        {slot ? `This tab's identity (${slot}) is not logged in any more. ` : "Not logged in. "}
        Open your join or login link (<code>/join#c=…</code>, <code>/login#t=vp_…</code>) or send a Bearer token.
      </p>
      <h2>Continue as someone logged in in this browser</h2>
      <IdentityList current={null} onPick={(i) => void switchTo(i)} />
      <p><a href="/login" data-testid="login-link">Log in</a></p>
    </main>
  );
}

/** `/api/me` failed for another reason than 401 (network down, server restarting, 5xx): the login screen would be
 * wrong, so say the server cannot be reached and offer Retry. */
function Unreachable({ error, retrying, onRetry }: { error: unknown; retrying: boolean; onRetry: () => void }) {
  const detail = error instanceof ApiError ? `HTTP ${error.status}: ${error.message}` : error instanceof Error ? error.message : String(error);
  return (
    <main className="login-needed" data-testid="server-unreachable">
      <h1>Vezérlőpult</h1>
      <p role="alert">Cannot reach the server. Check the connection, then retry.</p>
      <p style={{ color: "var(--muted)" }} data-testid="server-unreachable-detail">{detail}</p>
      <p>
        <button type="button" data-testid="server-retry" disabled={retrying} onClick={onRetry}>
          {retrying ? "Retrying…" : "Retry"}
        </button>
      </p>
    </main>
  );
}

export function Layout() {
  const loc = useLocation();
  const me = useMe();
  const qc = useQueryClient();
  // Live updates only once the identity is known, as that identity; a switch of identity reopens the stream.
  const identity = me.isSuccess ? `${me.data.actor}\u0000${me.data.slot ?? ""}` : null;
  useEffect(() => (identity === null ? undefined : startLive(qc)), [identity, qc]);
  const presence = useQuery({ queryKey: qk.presence, queryFn: () => unwrap(api.GET("/api/presence")),
    enabled: me.isSuccess, refetchInterval: 30_000 });
  // The badge is the server's uncapped unread count; one row is enough to get it.
  const unread = useQuery({ queryKey: [...qk.notifications, "unread-count"],
    queryFn: () => unwrap(api.GET("/api/notifications", { params: { query: { unread: true, limit: 1 } } })),
    enabled: me.isSuccess, refetchInterval: 60_000 });
  const unreadCount = unread.data?.unread_count ?? 0;

  useEffect(() => {
    if (!me.isSuccess) return;
    // Presence is best effort: a failed ping (network down, deploy restart) must never surface as an error.
    const ping = () => {
      api.POST("/api/presence", { body: { page: loc.pathname } }).catch(() => {});
    };
    ping();
    const t = window.setInterval(ping, 30_000);
    return () => window.clearInterval(t);
  }, [loc.pathname, me.isSuccess]);

  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) return <NotLoggedIn />;
    return <Unreachable error={me.error} retrying={me.isFetching} onRetry={() => void me.refetch()} />;
  }
  return (
    <div className="app">
      <nav aria-label="Main" className="nav">
        <span className="brand">Vezérlőpult</span>
        {NAV.map(([to, label]) => (
          <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}>{label}</NavLink>
        ))}
        <NavLink to="/notifications" data-testid="nav-notifications" aria-label={`Notifications: ${unreadCount} unread`}>
          Notifications ({unreadCount})
        </NavLink>
        <ul className="presence" aria-label="Who is online" data-testid="presence">
          {(presence.data ?? []).map((p) => (
            <li key={p.actor} className="chip" title={`${p.actor} on ${p.page}`}>{p.actor}</li>
          ))}
        </ul>
        <IdentityMenu me={me.data} />
      </nav>
      <ActionResult />
      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}
